import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyPolicies, GetMyPoliciesOutputType, acknowledgePolicyVersion } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Shield, Clock, AlertTriangle, CheckCircle2, Eye, FileText, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

type Outstanding = GetMyPoliciesOutputType['outstanding'][0];
type Acknowledged = GetMyPoliciesOutputType['acknowledged'][0];

export default function MyPolicies() {
  const { user, isLoading: authLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<GetMyPoliciesOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewPolicy, setViewPolicy] = useState<Outstanding | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [receipt, setReceipt] = useState<any>(null);

  useEffect(() => {
    if (!authLoading && !user) loginWithRedirect({ redirectUrl: window.location.href });
  }, [authLoading, user, loginWithRedirect]);

  useEffect(() => {
    if (!user) return;
    getMyPolicies({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (authLoading || !user) return null;
  if (loading) return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <h1 className="text-xl font-bold mb-4">My Policies</h1>
      <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
    </div>
  );

  const handleAcknowledge = async () => {
    if (!viewPolicy) return;
    setAcknowledging(true);
    try {
      const res = await acknowledgePolicyVersion({ assignmentId: viewPolicy.assignmentId, confirmAcknowledgement: true });
      if (res.alreadyAcknowledged) {
        toast.info('Already acknowledged');
      } else if (res.receipt) {
        setReceipt(res.receipt);
      }
      // Reload
      const d = await getMyPolicies({});
      setData(d);
      setShowConfirm(false);
    } catch (e: any) {
      toast.error(e.message || 'Acknowledgement failed');
    }
    setAcknowledging(false);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <h1 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2"><Shield className="w-5 h-5" />My Policies</h1>

      {/* Outstanding */}
      {data && data.outstanding.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            Requiring Acknowledgement ({data.outstanding.length})
          </h2>
          <div className="space-y-3">
            {data.outstanding.map(p => (
              <div key={p.assignmentId} className={`border rounded-lg p-4 cursor-pointer hover:bg-muted/30 transition-colors ${p.isOverdue ? 'border-destructive/50 bg-destructive/5' : 'border-border'}`} onClick={() => setViewPolicy(p)}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-foreground">{p.policyName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {p.policyNumber && <span className="text-xs text-muted-foreground">{p.policyNumber}</span>}
                      <span className="text-xs text-muted-foreground">v{p.versionNumber}</span>
                      {p.isMandatory && <Badge variant="outline" className="text-[10px] px-1 py-0">Mandatory</Badge>}
                    </div>
                  </div>
                  <div className="text-right">
                    {p.isOverdue ? (
                      <Badge variant="destructive" className="text-xs">Overdue</Badge>
                    ) : p.deadline ? (
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />Due {format(parseISO(p.deadline), 'd MMM yyyy')}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Already Acknowledged */}
      {data && data.acknowledged.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            Acknowledged ({data.acknowledged.length})
          </h2>
          <div className="space-y-2">
            {data.acknowledged.map(p => (
              <div key={p.assignmentId} className="border border-border rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{p.policyName}</p>
                  <p className="text-xs text-muted-foreground">v{p.versionNumber} • Acknowledged {p.acknowledgedAt ? format(parseISO(p.acknowledgedAt), 'd MMM yyyy') : '—'}</p>
                </div>
                {p.newVersionAvailable && <Badge className="bg-yellow-500/10 text-yellow-700 text-xs">New version available</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.outstanding.length === 0 && data.acknowledged.length === 0 && (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No policies assigned to you yet.</p>
        </div>
      )}

      {/* Read & Acknowledge Dialog */}
      {viewPolicy && !receipt && (
        <Dialog open onOpenChange={() => { setViewPolicy(null); setShowConfirm(false); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />{viewPolicy.policyName}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                {viewPolicy.policyNumber && <span className="text-sm text-muted-foreground">{viewPolicy.policyNumber}</span>}
                <span className="text-sm text-muted-foreground">Version {viewPolicy.versionNumber}</span>
                {viewPolicy.effectiveDate && <span className="text-sm text-muted-foreground">• Effective {format(parseISO(viewPolicy.effectiveDate), 'd MMM yyyy')}</span>}
              </div>
            </DialogHeader>

            {/* Policy Content */}
            <div className="border border-border rounded-lg p-4 my-4 max-h-[40vh] overflow-y-auto">
              <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap text-sm">
                {viewPolicy.policyContent || 'Policy content not available.'}
              </div>
            </div>

            {/* Declaration */}
            {!showConfirm ? (
              <DialogFooter>
                <Button variant="outline" onClick={() => { setViewPolicy(null); }}>Close</Button>
                <Button onClick={() => setShowConfirm(true)}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />Acknowledge This Policy
                </Button>
              </DialogFooter>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm font-semibold mb-2">Declaration</p>
                  <p className="text-sm text-foreground italic">{viewPolicy.declarationWording}</p>
                </div>
                <p className="text-xs text-muted-foreground">By clicking &quot;Confirm Acknowledgement&quot; below, you confirm that you have read and understood this policy.</p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowConfirm(false)}>Go Back</Button>
                  <Button onClick={handleAcknowledge} disabled={acknowledging}>
                    {acknowledging ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Confirm Acknowledgement
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Receipt */}
      {receipt && (
        <Dialog open onOpenChange={() => { setReceipt(null); setViewPolicy(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" />Acknowledgement Confirmed</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="bg-primary/5 rounded-lg p-4 space-y-2">
                <Row label="Policy" value={receipt.policyName} />
                <Row label="Version" value={receipt.versionNumber} />
                <Row label="Acknowledged" value={format(parseISO(receipt.acknowledgedAt), "d MMM yyyy 'at' HH:mm")} />
                <Row label="Reference" value={receipt.auditReference} />
              </div>
              <p className="text-xs text-muted-foreground">This record has been stored. You may close this window.</p>
            </div>
            <DialogFooter>
              <Button onClick={() => { setReceipt(null); setViewPolicy(null); }}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}
